import React, { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { Check, Gem } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";

const TIER_COLOR = { 1: "#A1A1AA", 2: "#00E5FF", 3: "#16A34A", 4: "#FFD700" };

export default function Shop() {
  const { setCharacter } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("fashion");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/shop");
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const buy = async (id, title) => {
    setBusy(id);
    try {
      const { data: r } = await api.post("/shop/buy", { item_id: id });
      setCharacter(r.character);
      toast.success(`Copped ${title} 🔥 Flex activated.`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  if (!data) return null;
  const items = tab === "fashion" ? data.fashion : data.cars;
  const owned = new Set(data.owned);

  return (
    <div className="px-5 pt-6" data-testid="shop-page">
      <div className="flex items-center justify-between">
        <div>
          <span className="label-xs text-slate-500">Market · Flex Up</span>
          <h1 className="font-display text-3xl mt-1">The <span className="text-[#FFD700]">Plug</span></h1>
        </div>
        <div className="text-right">
          <div className="text-[#16A34A] font-display text-sm">{naira(data.wallet)}</div>
          <div className="text-[#FFD700] font-display text-sm flex items-center gap-1 justify-end"><Gem size={12} /> {data.gems}</div>
        </div>
      </div>

      <div className="mt-5 flex gap-2 bg-white border border-slate-200 rounded-full p-1">
        {["fashion", "cars"].map((t) => (
          <button key={t} data-testid={`shop-tab-${t}`} onClick={() => setTab(t)}
            className={`flex-1 py-2.5 rounded-full label-xs transition-colors ${tab === t ? "bg-[#FFD700] text-slate-900" : "text-slate-500"}`}>
            {t === "fashion" ? "Fashion" : "Whips"}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3 mb-4">
        {items.map((it) => {
          const Icon = Icons[it.icon.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("")] || Icons.ShoppingBag;
          const has = owned.has(it.id);
          const afford = data.wallet >= it.price;
          return (
            <div key={it.id} data-testid={`item-${it.id}`}
              className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-black/60 flex items-center justify-center shrink-0" style={{ boxShadow: `inset 0 0 0 1px ${TIER_COLOR[it.tier]}40` }}>
                  <Icon size={22} style={{ color: TIER_COLOR[it.tier] }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{it.title}</div>
                  <div className="flex items-center gap-3 label-xs text-slate-400 mt-1">
                    <span className="text-[#FF0055]">+{it.clout} clout</span>
                    {it.upkeep && <span>⛽ {naira(it.upkeep)}/wk</span>}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <span className="font-display text-[#16A34A]">{naira(it.price)}</span>
                {has ? (
                  <span className="label-xs text-[#16A34A] flex items-center gap-1"><Check size={13} /> Owned</span>
                ) : (
                  <button data-testid={`buy-${it.id}`} onClick={() => buy(it.id, it.title)} disabled={!afford || busy === it.id}
                    className={`px-5 py-2 rounded-lg label-xs transition-colors ${afford ? "bg-[#FFD700] text-slate-900 hover:bg-[#ffe34d]" : "bg-black/40 text-slate-400"}`}>
                    {busy === it.id ? "…" : afford ? "Buy" : "Too rich"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
